import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    Plus, Eye, MoreVertical, Send, CheckCircle,
    XCircle, FileText, Truck, Clock
} from 'lucide-react';
import { PageHeader } from '../../components/layout';
import {
    Card, Table, Pagination, Button, Badge,
    SearchInput, Select, Dropdown, DropdownItem, DropdownDivider,
    ConfirmDialog, toast
} from '../../components/common';
import { purchaseOrderApi } from '../../services/api';
import './PurchaseOrders.css';

export default function PurchaseOrderList() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [actionPO, setActionPO] = useState(null);
    const [actionType, setActionType] = useState('');

    const { data, isLoading } = useQuery({
        queryKey: ['purchaseOrders', page, pageSize, search, statusFilter],
        queryFn: () => purchaseOrderApi.list({ page, limit: pageSize, search, status: statusFilter }),
        select: (res) => res.data
    });

    const actionMutation = useMutation({
        mutationFn: ({ id, action }) => {
            switch (action) {
                case 'submit': return purchaseOrderApi.submit(id);
                case 'approve': return purchaseOrderApi.approve(id);
                case 'send': return purchaseOrderApi.send(id, {});
                case 'cancel': return purchaseOrderApi.cancel(id, { reason: 'Cancelled' });
                default: throw new Error('Unknown action');
            }
        },
        onSuccess: (_, { action }) => {
            const msgs = { submit: 'submitted', approve: 'approved', send: 'sent to vendor', cancel: 'cancelled' };
            toast.success(`PO ${msgs[action]} successfully`);
            queryClient.invalidateQueries(['purchaseOrders']);
            setActionPO(null);
            setActionType('');
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Action failed');
        }
    });

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric'
        });
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency', currency: 'IDR', minimumFractionDigits: 0
        }).format(value || 0);
    };

    const getStatusBadge = (status) => {
        const config = {
            'draft': 'default',
            'pending_approval': 'warning',
            'approved': 'info',
            'sent': 'info',
            'acknowledged': 'success',
            'partially_received': 'warning',
            'completed': 'success',
            'cancelled': 'danger'
        };
        return <Badge variant={config[status] || 'default'}>{status?.replace('_', ' ')}</Badge>;
    };

    const columns = [
        {
            key: 'poNumber',
            title: 'PO Number',
            dataIndex: 'poNumber',
            render: (num) => <span className="po-number">{num}</span>
        },
        {
            key: 'vendor',
            title: 'Vendor',
            dataIndex: 'vendor',
            render: (vendor) => <span>{vendor?.name}</span>
        },
        {
            key: 'totalAmount',
            title: 'Total',
            dataIndex: 'totalAmount',
            align: 'right',
            render: (val) => <span className="po-total">{formatCurrency(val)}</span>
        },
        {
            key: 'deliveryDate',
            title: 'Delivery Date',
            dataIndex: 'deliveryDate',
            render: (date) => formatDate(date)
        },
        {
            key: 'status',
            title: 'Status',
            dataIndex: 'status',
            render: (status) => getStatusBadge(status)
        },
        {
            key: 'actions',
            title: '',
            width: 50,
            align: 'center',
            render: (_, row) => (
                <Dropdown
                    trigger={<button className="action-btn"><MoreVertical size={16} /></button>}
                    align="right"
                >
                    {({ close }) => (
                        <>
                            <DropdownItem icon={Eye} onClick={() => { close(); navigate(`/purchase-orders/${row.id}`); }}>
                                View Details
                            </DropdownItem>
                            {row.status === 'draft' && (
                                <DropdownItem icon={Send} onClick={() => { close(); setActionPO(row); setActionType('submit'); }}>
                                    Submit
                                </DropdownItem>
                            )}
                            {row.status === 'pending_approval' && (
                                <DropdownItem icon={CheckCircle} onClick={() => { close(); setActionPO(row); setActionType('approve'); }}>
                                    Approve
                                </DropdownItem>
                            )}
                            {row.status === 'approved' && (
                                <DropdownItem icon={Send} onClick={() => { close(); setActionPO(row); setActionType('send'); }}>
                                    Send to Vendor
                                </DropdownItem>
                            )}
                            {['draft', 'pending_approval'].includes(row.status) && (
                                <>
                                    <DropdownDivider />
                                    <DropdownItem icon={XCircle} onClick={() => { close(); setActionPO(row); setActionType('cancel'); }}>
                                        Cancel
                                    </DropdownItem>
                                </>
                            )}
                        </>
                    )}
                </Dropdown>
            )
        }
    ];

    const statusOptions = [
        { value: '', label: 'All Status' },
        { value: 'draft', label: 'Draft' },
        { value: 'pending_approval', label: 'Pending Approval' },
        { value: 'approved', label: 'Approved' },
        { value: 'sent', label: 'Sent' },
        { value: 'acknowledged', label: 'Acknowledged' },
        { value: 'completed', label: 'Completed' },
        { value: 'cancelled', label: 'Cancelled' }
    ];

    return (
        <div className="po-page">
            <PageHeader
                title="Purchase Orders"
                subtitle="Manage purchase orders"
                breadcrumbs={[{ label: 'Purchase Orders', path: '/purchase-orders' }]}
                actions={
                    <Button icon={Plus} onClick={() => navigate('/purchase-orders/create')}>
                        Create PO
                    </Button>
                }
            />

            <Card>
                <div className="po-toolbar">
                    <SearchInput
                        value={search}
                        onChange={setSearch}
                        onSearch={setSearch}
                        placeholder="Search PO..."
                        className="po-search"
                    />
                    <Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        options={statusOptions}
                        className="po-filter"
                    />
                </div>

                <Table
                    columns={columns}
                    data={data?.data || []}
                    loading={isLoading}
                    emptyText="No purchase orders found"
                    rowKey="id"
                />

                {data?.meta && (
                    <div className="po-pagination">
                        <Pagination
                            page={page}
                            totalPages={data.meta.totalPages}
                            pageSize={pageSize}
                            total={data.meta.total}
                            onPageChange={setPage}
                            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                        />
                    </div>
                )}
            </Card>

            <ConfirmDialog
                isOpen={!!actionPO}
                onClose={() => { setActionPO(null); setActionType(''); }}
                onConfirm={() => actionMutation.mutate({ id: actionPO?.id, action: actionType })}
                title={`${actionType.charAt(0).toUpperCase() + actionType.slice(1)} PO`}
                message={`Are you sure you want to ${actionType} this purchase order?`}
                confirmText={actionType.charAt(0).toUpperCase() + actionType.slice(1)}
                variant={actionType === 'cancel' ? 'danger' : 'primary'}
                loading={actionMutation.isPending}
            />
        </div>
    );
}
